import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Heart, Menu, Search, ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "./ui/sheet";
import { Logo } from "./Logo";
import { SearchDialog } from "./SearchDialog";
import { useStore } from "../context/StoreContext";

const NAV = [
  ["/product/pustak-rack", "Pustak Rack"],
  ["/shop", "Shop"],
  ["/#how-it-builds", "How it builds"],
  ["/blog", "Blog"],
  ["/#faq", "FAQ"],
  ["/story", "Our story"],
];

const IconBtn = ({ children, ...props }) => (
  <button className="relative grid h-10 w-10 place-items-center rounded-full transition-[background-color,transform] duration-200 hover:bg-walnut/5 active:scale-95" {...props}>
    {children}
  </button>
);

const Badge = ({ n, testid }) =>
  n > 0 ? (
    <motion.span key={n} initial={{ scale: 0.6 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 500, damping: 18 }} data-testid={testid} className="absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-sheesham px-1 text-[10px] font-bold text-linen">{n}</motion.span>
  ) : null;

const MESSAGES = ["24 Hrs free delivery across Hyderabad", "No carpenter needed", "Code HW10 for 10% off"];

const Announcement = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % MESSAGES.length), 3500);
    return () => clearInterval(t);
  }, []);
  return (
    <div data-testid="announcement-bar" className="bg-walnut text-linen">
      <div className="container-hw flex h-9 items-center justify-center text-xs font-medium sm:hidden">
        <motion.span key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>{MESSAGES[i]}</motion.span>
      </div>
      <div className="container-hw hidden h-9 items-center justify-center gap-6 text-xs font-medium sm:flex">
        {MESSAGES.map((m, k) => <span key={m} className="flex items-center gap-6">{k > 0 && <span className="h-1 w-1 rounded-full bg-brass" />}{m}</span>)}
      </div>
    </div>
  );
};

export const Header = () => {
  const { count, wishlist, setCartOpen } = useStore();
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 12);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <header className="sticky top-0 z-40">
      <Announcement />
      <div className={`glass border-b transition-[border-color,box-shadow] duration-300 ${scrolled ? "border-jute/60 shadow-[0_8px_30px_-16px_rgba(46,33,26,0.25)]" : "border-transparent"}`}>
        <div className="container-hw flex h-16 items-center justify-between gap-4 lg:h-[72px]">
          <div className="flex items-center gap-1">
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-button" className="grid h-10 w-10 place-items-center rounded-full hover:bg-walnut/5 lg:hidden" aria-label="Open menu"><Menu className="h-5 w-5" /></button>
              </SheetTrigger>
              <SheetContent side="left" className="flex w-[88vw] max-w-sm flex-col bg-linen p-6">
                <SheetTitle className="sr-only">Menu</SheetTitle>
                <Logo onClick={() => setMenuOpen(false)} />
                <nav className="mt-10 flex flex-col">
                  {NAV.map(([to, label], i) => (
                    <motion.div key={to} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i, duration: 0.35 }}>
                      <Link to={to} onClick={() => setMenuOpen(false)} data-testid={`mobile-nav-${label.toLowerCase().replace(/ /g, "-")}`} className="flex items-center justify-between border-b border-jute/50 py-4 font-sans text-xl font-medium">{label}<ArrowRight className="h-5 w-5 text-brass" /></Link>
                    </motion.div>
                  ))}
                </nav>
                <Link to="/product/pustak-rack" onClick={() => setMenuOpen(false)} data-testid="mobile-nav-cta" className="btn-sheesham mt-auto w-full py-3.5">Shop the Pustak Rack</Link>
              </SheetContent>
            </Sheet>
            <Logo />
          </div>
          <nav className="hidden items-center gap-1 lg:flex">
            {NAV.map(([to, label]) => {
              const active = !to.includes("#") && pathname.startsWith(to.split("?")[0]);
              return (
                <NavLink key={to} to={to} data-testid={`nav-${label.toLowerCase().replace(/ /g, "-")}`} className={`relative rounded-full px-3.5 py-2 text-sm font-medium transition-colors hover:text-sheesham ${active ? "text-sheesham" : "text-walnut/80"}`}>
                  {label}
                  {active && <motion.span layoutId="nav-pill" className="absolute inset-0 -z-10 rounded-full bg-sheesham/10" transition={{ type: "spring", stiffness: 400, damping: 30 }} />}
                </NavLink>
              );
            })}
          </nav>
          <div className="flex items-center gap-0.5">
            <IconBtn data-testid="search-open-button" aria-label="Search" onClick={() => setSearchOpen(true)}><Search className="h-5 w-5" /></IconBtn>
            <Link to="/wishlist" data-testid="wishlist-link" aria-label="Wishlist" className="relative grid h-10 w-10 place-items-center rounded-full transition-colors hover:bg-walnut/5">
              <Heart className="h-5 w-5" />
              <Badge n={wishlist.length} testid="wishlist-count" />
            </Link>
            <IconBtn data-testid="cart-drawer-trigger" aria-label="Open cart" onClick={() => setCartOpen(true)}>
              <ShoppingBag className="h-5 w-5" />
              <Badge n={count} testid="cart-count" />
            </IconBtn>
            <Link to="/product/pustak-rack" data-testid="header-cta" className="btn-sheesham ml-2 hidden px-5 py-2.5 md:inline-flex">Shop rack</Link>
          </div>
        </div>
      </div>
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </header>
  );
};
